import {
    IHttpActionOptions,
    IRequestHeaders,
    IFetchOptions,
    IFetchResponse,
} from '../interfaces';
import { HttpMethod } from '../types';
import { RestBadHttpActionParams, RestClientError } from '../helpers/errors';

export default class FetchClientService {
    private static validateEndpoint(endpoint: string) {
        if (endpoint.startsWith('/')) return;
        const errorMessage = `Invalid endpoint "${endpoint}". Please replace with "/${endpoint}"`;
        throw new RestBadHttpActionParams(endpoint, errorMessage);
    }

    private static buildUrl(endpoint: string, options: IHttpActionOptions) {
        const baseDomain: string = process.env.DOMAIN ?? '';
        const privateDomain: string = process.env.PRIVATE_DOMAIN ?? '';
        const defaultDomain =
            options.privateDomain === false ? baseDomain : privateDomain;
        return `${options.baseDomain || defaultDomain}${endpoint}`;
    }

    private static baseHeaders(options: IHttpActionOptions) {
        const baseHeaders: IRequestHeaders = {
            'Content-Type': 'application/json',
        };

        const customContentTypeHeader =
            options.headers?.['content-type'] ||
            options.headers?.['Content-Type'];

        if (customContentTypeHeader) {
            baseHeaders['Content-Type'] = customContentTypeHeader;
        }

        if (options.useApiKey && process.env.API_KEY)
            baseHeaders['x-api-key'] = process.env.API_KEY;

        const serviceNameEnv = process.env.SERVICE_NAME?.trim();
        const hostnameEnv = process.env.HOSTNAME?.trim();

        if (!baseHeaders['q-service-name'] && serviceNameEnv?.length) {
            baseHeaders['q-service-name'] = serviceNameEnv;
        }
        if (!baseHeaders['q-hostname'] && hostnameEnv?.length) {
            baseHeaders['q-hostname'] = hostnameEnv;
        }

        return baseHeaders;
    }

    private static customHeaders(options: IHttpActionOptions) {
        const {
            // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
            'Content-Type': _ContentType,
            // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
            'content-type': _contentType,
            ...customHeadersObject
        } = options.headers || {};

        return customHeadersObject;
    }

    private static internalHeaders() {
        const internalHeaders: IRequestHeaders = {};

        const serviceNameEnv = process.env.SERVICE_NAME?.trim();
        const hostnameEnv = process.env.HOSTNAME?.trim();

        if (serviceNameEnv?.length) {
            internalHeaders['q-service-name'] = serviceNameEnv;
        }
        if (hostnameEnv?.length) {
            internalHeaders['q-hostname'] = hostnameEnv;
        }

        return internalHeaders;
    }

    private static buildFetchOptions(
        method: string,
        body: unknown,
        options: IHttpActionOptions,
    ): IFetchOptions {
        const headers = {
            ...this.baseHeaders(options),
            ...this.customHeaders(options),
            ...this.internalHeaders(),
        };

        /**
         * * exclude headers with invalid values
         */
        const filteredHeaders = Object.fromEntries(
            Object.entries(headers).filter(
                ([, value]) => value !== null && value !== undefined,
            ),
        );

        const requestOptions: IFetchOptions = {
            headers: filteredHeaders,
            method,
        };
        if (body) requestOptions['body'] = JSON.stringify(body);
        return requestOptions;
    }

    private static isValidUrl(url: URL) {
        return !!(url.protocol && url.hostname && url.pathname);
    }

    private static hasValidProtocol(url: URL) {
        const secureProtocols = ['http:', 'https:'];
        return secureProtocols.includes(url.protocol);
    }

    private static verifyUrl(urlObj: URL): void {
        if (!this.isValidUrl(urlObj)) {
            throw new RestBadHttpActionParams(
                'Invalid URL',
                `URL details: Protocol: ${urlObj.protocol}, Hostname: ${urlObj.hostname}, Pathname: ${urlObj.pathname}`,
            );
        }
        if (!this.hasValidProtocol(urlObj)) {
            throw new RestBadHttpActionParams(
                'Invalid URL',
                `URL protocol [${urlObj.protocol}] is not secure`,
            );
        }
    }

    private static toHttpUrl(url: string) {
        try {
            const urlObj = new URL(url);
            this.verifyUrl(urlObj);
            return urlObj.href;
        } catch (error) {
            throw new RestBadHttpActionParams(
                `Invalid URL [${url}]`,
                'URL is not valid.',
                error,
            );
        }
    }

    private static isFileDownload(
        contentType: string | null,
        contentDisposition: string | null,
    ) {
        return (
            contentDisposition?.includes('attachment') ||
            contentType?.includes('application/octet-stream') ||
            contentType?.includes('text/csv') ||
            contentType?.includes('application/vnd.ms-excel') ||
            contentType?.includes('application/vnd.openxmlformats') ||
            contentType?.includes('application/zip') ||
            contentType?.includes('application/pdf')
        );
    }

    private static async getResponseData(
        isJsonResponse: boolean,
        response: Response,
        options: IHttpActionOptions,
    ) {
        try {
            if (options.returnRawResponse) {
                return response;
            }

            const contentType = response.headers.get('content-type');
            const contentDisposition = response.headers.get(
                'content-disposition',
            );
            const contentEncoding = response.headers.get('content-encoding');

            const isFileDownload = this.isFileDownload(
                contentType,
                contentDisposition,
            );

            if (options.skipJsonParsing && contentEncoding) {
                return await response.arrayBuffer();
            }

            if (options.skipJsonParsing && isFileDownload) {
                return await response.arrayBuffer();
            }

            if (options.skipJsonParsing) {
                return await response.text();
            }

            const text = await response.text();

            if (isJsonResponse) {
                try {
                    return JSON.parse(text);
                } catch {
                    return text;
                }
            }
            return text;
        } catch (error) {
            console.error('Error processing response data:', error);
            return null;
        }
    }

    private static getResponseHeaders(
        response: Response,
    ): Record<string, string> {
        const headers: Record<string, string> = {};
        response.headers.forEach((value, key) => {
            headers[key] = value;
        });
        return headers;
    }

    private static async handleResponse(
        response: Response,
        httpActionOptions: IHttpActionOptions,
    ): Promise<IFetchResponse | unknown> {
        const responseContentType = response.headers.get('content-type');
        const isJsonResponse =
            responseContentType &&
            responseContentType.includes('application/json') &&
            !httpActionOptions.skipJsonParsing;

        const responseData = await this.getResponseData(
            !!isJsonResponse,
            response,
            httpActionOptions,
        );

        if (response.ok) {
            if (httpActionOptions.includeHttpResponseDetails) {
                const headers = this.getResponseHeaders(response);
                return {
                    status: response.status,
                    data: responseData,
                    headers,
                };
            }
            return responseData;
        }

        throw new RestClientError(
            response.url,
            responseData ?? {},
            response.status,
        );
    }

    private static fetchData(
        url: string,
        {
            fetchOptions,
            httpActionOptions,
        }: {
            fetchOptions: IFetchOptions;
            httpActionOptions: IHttpActionOptions;
        },
    ) {
        return fetch(url, fetchOptions)
            .then((response) =>
                this.handleResponse(response, httpActionOptions),
            )
            .catch((error) => {
                if (error instanceof RestClientError) throw error;
                throw new RestClientError(url, error);
            });
    }

    private static queryParamsToQueryString(
        queryParameters: Record<string, string | string[]>,
    ) {
        const queryParamsArray = Object.entries(queryParameters).map(
            ([queryName, queryValue]) => {
                const isArrayValue = Array.isArray(queryValue);
                if (isArrayValue)
                    return queryValue
                        .map((val) => `${queryName}=${val}`)
                        .join('&');
                else return `${queryName}=${queryValue}`;
            },
        );
        return queryParamsArray.join('&');
    }

    public static httpAction(
        method: HttpMethod,
        endpoint: string,
        body: unknown,
        options: IHttpActionOptions = {},
    ) {
        this.validateEndpoint(endpoint);
        const url = this.buildUrl(endpoint, options);
        const separator = url.includes('?') ? '&' : '?';
        const queryParamsString = options.queryParameters
            ? `${separator}${this.queryParamsToQueryString(
                  options.queryParameters,
              )}`
            : '';
        const httpUrl = this.toHttpUrl(`${url}${queryParamsString}`);
        const fetchDataOptions = {
            fetchOptions: this.buildFetchOptions(
                method.toUpperCase(),
                body,
                options,
            ),
            httpActionOptions: options,
        };
        return this.fetchData(httpUrl, fetchDataOptions);
    }
}
